import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import InstallmentModal from './InstallmentModal';
import SaleBikeModal from './SaleBikeModal';
import EditSaleModal from './EditSaleModal'; 

const SalesInventory = () => {
    const [sales, setSales] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSale, setSelectedSale] = useState(null);
    const [showSaleModal, setShowSaleModal] = useState(false);
    const [selectedEditSale, setSelectedEditSale] = useState(null); 
    const [loading, setLoading] = useState(false);
    
    const [canDelete, setCanDelete] = useState(false); 
    const [canEdit, setCanEdit] = useState(false);
    const limit = 15;

    const checkUserPermissions = async () => {
        try {
            const res = await API.get('/v1/sales/check-permissions');
            if (res.data.success) {
                setCanDelete(res.data.canDelete);
                setCanEdit(res.data.canEdit);
            }
        } catch (err) {
            console.error("Permission Check Error:", err);
        }
    };

    const fetchSales = async () => {
        setLoading(true);
        try {
            const res = await API.get('/v1/sales', {
                params: { page, limit, search: searchTerm }
            });
            
            if (res.data.success) {
                setSales(res.data.data);
                setTotalPages(res.data.totalPages || 1);
            }
        } catch (err) {
            console.error("Fetch Sales Error:", err);
        }
        setLoading(false);
    };

    const handleDeleteSale = async (id) => {
        if (window.confirm("Are you sure you want to delete this sale record? All related transactions will be reversed.")) {
            try {
                const res = await API.delete(`/v1/sales/${id}`);
                if (res.data.success) {
                    alert("Record Deleted Successfully");
                    fetchSales();
                }
            } catch (err) {
                alert("Delete failed: " + (err.response?.data?.error || "Server Error"));
            }
        }
    };

    const handleEditSale = (sale) => {
        setSelectedEditSale(sale); 
    };

    useEffect(() => {
        checkUserPermissions(); 
    }, []);

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            fetchSales();
        }, 500);
        return () => clearTimeout(delaySearch);
    }, [page, searchTerm]);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h3 style={styles.title}>Sales Inventory</h3>
                    <p style={{fontSize: '11px', color: '#94a3b8', margin: 0}}>Manage customer records and vehicle payments</p>
                </div>
                <div style={styles.headerActions}>
                    <input 
                        type="text" 
                        placeholder="Search Name, CNIC, Engine..." 
                        style={styles.searchBar} 
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1); 
                        }}
                    />
                    <button onClick={() => setShowSaleModal(true)} style={styles.mainBtn}>+ New Sale</button>
                </div>
            </div>

            <div style={styles.card}>
                <div style={styles.scrollWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th style={styles.th}>Customer Details</th>
                                <th style={styles.th}>Vehicle Info</th>
                                <th style={styles.th}>Sale Mode</th>
                                <th style={styles.th}>Net Amount</th>
                                <th style={styles.th}>Remaining</th>
                                <th style={{...styles.th, textAlign: 'center'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" style={styles.loader}>Loading...</td></tr>
                            ) : sales.length > 0 ? sales.map(s => (
                                <tr key={s.id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={styles.pText}>{s.customer_name}</div>
                                        <div style={styles.sText}>CNIC: {s.cnic}</div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.pText}>{s.model_name}</div>
                                        <div style={styles.sText}>Eng: {s.engine_number}</div>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={s.sale_type?.toLowerCase() === 'installment' ? styles.bBlue : styles.bGrey}>
                                            {s.sale_type}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.amt}>{parseInt(s.net_amount || 0).toLocaleString()}</span>
                                    </td>
                                    <td style={styles.td}>
                                        {parseInt(s.remaining_balance || 0) > 0 ? (
                                            <span style={styles.due}>{parseInt(s.remaining_balance).toLocaleString()}</span>
                                        ) : (
                                            <span style={styles.paid}>Paid</span>
                                        )}
                                    </td>
                                    <td style={{...styles.td, textAlign: 'center', display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center'}}>
                                        {s.sale_type?.toLowerCase() === 'installment' && (
                                            <button onClick={() => setSelectedSale(s)} style={styles.actBtn}>Ledger</button>
                                        )}
                                        {canEdit && (
                                            <button onClick={() => handleEditSale(s)} style={styles.editBtn}>Edit</button>
                                        )}
                                        {canDelete && (
                                            <button onClick={() => handleDeleteSale(s.id)} style={styles.delBtn}>Delete</button>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="6" style={{textAlign:'center', padding:'30px', color: '#64748b'}}>No records found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.pagination}>
                    <span style={styles.pText}>Page {page} of {totalPages}</span>
                    <div style={styles.btnGroup}>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={page === 1 ? styles.disBtn : styles.pgBtn}>Back</button>
                        <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={page === totalPages ? styles.disBtn : styles.pgBtn}>Next</button>
                    </div>
                </div>
            </div>

            {showSaleModal && <SaleBikeModal onClose={() => setShowSaleModal(false)} onRefresh={fetchSales} />}
            {selectedSale && <InstallmentModal sale={selectedSale} onClose={() => { setSelectedSale(null); fetchSales(); }} />}
            {selectedEditSale && (
                <EditSaleModal 
                    sale={selectedEditSale} 
                    onClose={() => setSelectedEditSale(null)} 
                    onRefresh={fetchSales} 
                />
            )}
        </div>
    );
};

const styles = {
    container: { width: '100%', boxSizing: 'border-box' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', padding: '0 5px' },
    title: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 },
    headerActions: { display: 'flex', gap: '10px' },
    searchBar: { padding: '8px 15px', borderRadius: '8px', border: '1px solid #cbd5e1', width: '250px', fontSize: '13px', outline: 'none' },
    mainBtn: { background: '#2563eb', color: '#fff', padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '12px' },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', width: '100%', overflow: 'hidden' },
    scrollWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '800px' },
    tableHeader: { background: '#f8fafc' },
    th: { padding: '14px 15px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '12px 15px', fontSize: '13px', verticalAlign: 'middle' },
    pText: { fontWeight: '600', color: '#334155', fontSize: '13px', margin: 0 },
    sText: { fontSize: '11px', color: '#94a3b8', marginTop: '2px' },
    amt: { fontWeight: '700', color: '#334155' },
    due: { color: '#e11d48', fontWeight: '700', background: '#fff1f2', padding: '2px 6px', borderRadius: '4px' },
    paid: { color: '#059669', fontWeight: '700', background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px' },
    bBlue: { background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' },
    bGrey: { background: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' },
    actBtn: { background: '#1e293b', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '11px' },
    editBtn: { background: '#0284c7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '11px' },
    delBtn: { background: '#be123c', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '11px' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', borderTop: '1px solid #f1f5f9' },
    btnGroup: { display: 'flex', gap: '8px' },
    pgBtn: { padding: '6px 15px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    disBtn: { padding: '6px 15px', borderRadius: '6px', border: '1px solid #f1f5f9', background: '#f8fafc', color: '#cbd5e1', cursor: 'not-allowed', fontSize: '12px' },
    loader: { textAlign: 'center', padding: '30px', color: '#64748b' }
};

export default SalesInventory;